/**
 * Cinqo Design Tokens — export TypeScript
 * ========================================
 * Miroir type des tokens CSS pour consommation programmatique :
 *   - couleurs de charts/graphs (ex: ApexCharts, Recharts)
 *   - styles inline dynamiques (quand Tailwind n'est pas utilisable)
 *   - gen de themes (PDF, email HTML, exports)
 *
 * Source de verite : packages/brand/tokens.css
 * Ce fichier est un MIRROR typescript. Quand on modifie les tokens,
 * les 3 fichiers (tokens.css, tailwind-preset.js, tokens.ts) bougent
 * ensemble — memes valeurs.
 *
 * Preferer les classes Tailwind (bg-violet-500) dans le JSX.
 * N'utiliser `tokens` qu'en dernier recours.
 */

export const tokens = {
  /* ═══ TIER 1 — PRIMITIVES ═══ */
  color: {
    violet: {
      50:  '#F5F4FF',
      100: '#D4D1F9',
      200: '#B0ABF2',
      300: '#8B83EB',
      400: '#6558DF',
      500: '#3D2ECC',
      600: '#2E22A8',
      700: '#221840',
      800: '#160F2A',
      900: '#0B0618',
    },
    mint: {
      50:  '#E5FAF5',
      100: '#C0F2E4',
      200: '#7DDFC4',
      300: '#45D4B1',
      400: '#2EC9A0',
      500: '#20B088',
      600: '#17896A',
      700: '#0F5E49',
      800: '#083D30',
      900: '#031F18',
    },
    neutral: {
      0:   '#FFFFFF',
      50:  '#F0F1F9',
      100: '#E8E9F0',
      200: '#C4C6D0',
      300: '#A0A3B0',
      500: '#747585',
      700: '#3A3A50',
      900: '#141220',
    },
  },

  /* ═══ TIER 2 — SEMANTIC ═══ */
  surface: {
    default: '#FCF8FF',
    dim:     '#DBD8E4',
    low:     '#F5F2FE',
    mid:     '#EFECF8',
    high:    '#EAE6F2',
    top:     '#E4E1ED',
    card:    '#FFFFFF',
  },

  ink: {
    default:   '#1B1B23',
    secondary: '#464555',
    muted:     '#777586',
    ghost:     '#C7C4D7',
  },

  status: {
    success:    { fg: '#42BC65', bg: '#E0F5E9' },
    warning:    { fg: '#F5A623', bg: '#FFF4E0' },
    error:      { fg: '#E24B3C', bg: '#FDECEA' },
    info:       { fg: '#3D2ECC', bg: '#F5F4FF' },
  },

  /* ═══ TYPOGRAPHIE ═══ */
  font: {
    sans: "'Inter', system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
    mono: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
  },

  text: {
    h1:      { size: '32px', weight: 700, lineHeight: 1.2 },
    h2:      { size: '24px', weight: 700, lineHeight: 1.3 },
    h3:      { size: '17px', weight: 400, lineHeight: 1.4 },
    body:    { size: '14px', weight: 400, lineHeight: 1.5 },
    data:    { size: '13px', weight: 400, lineHeight: 1.5, letterSpacing: '0.02em' },
    caption: { size: '12px', weight: 400, lineHeight: 1.4 },
    label:   { size: '10px', weight: 400, lineHeight: 1.3, letterSpacing: '0.05em' },
  },

  /* ═══ RADIUS / SHADOW ═══ */
  radius: {
    sm: '4px',
    md: '6px',
    lg: '8px',
    xl: '12px',
  },

  shadow: {
    sm:      '0 1px 3px rgba(61, 46, 204, 0.08)',
    md:      '0 4px 12px rgba(61, 46, 204, 0.06)',
    lg:      '0 8px 24px rgba(61, 46, 204, 0.10)',
    ambient: '0 12px 40px rgba(27, 27, 35, 0.06)',
  },

  gradient: {
    brand: 'linear-gradient(135deg, #3D2ECC 0%, #221840 100%)',
  },
} as const

/**
 * Brand palette applicative — alias nommes pour consommation directe.
 *
 * Reunit les valeurs utilisees jour 1 par le marketing / les PDF / les
 * surfaces "premium" (audit report, login hero, factures imprimees). Ces
 * valeurs sont legerement distinctes du tiers primitif `tokens.color.violet`
 * (qui suit une echelle Material-50-to-900 canonique) : on les conserve
 * separees pour preserver la direction artistique historique.
 *
 * A utiliser partout ou un hex brut est requis (SVG fill, inline style,
 * jsPDF, email HTML). Pour du JSX tailwind-friendly, prefere les classes.
 */
export const brandPalette = {
  /** Couleur primaire (= tokens.color.violet.500) — logos, CTA, headers. */
  primary:      '#3D2ECC',
  /** Violet profond — debut de gradient hero, logo Login ovale. */
  primaryDeep:  '#2218A0',
  /** Violet clair — highlight gradient hero. */
  primaryLight: '#5B47E0',
  /** Tint ultra-clair — decor RSE, backgrounds de badges "draft". */
  tint50:       '#F4F3FF',
  /** Tint clair — bandes facture, KPI cards, badges role "admin"/"gerant". */
  tint100:      '#EEF0FF',
} as const

/**
 * Gradients brand pre-composes.
 *
 * - `hero`  : gradient premium (LoginPage, cover rapport audit)
 * - `brand` : gradient duo violet 500→800 (surfaces presentation)
 */
export const brandGradient = {
  hero:  'linear-gradient(135deg, #2218A0 0%, #3D2ECC 45%, #5B47E0 100%)',
  brand: 'linear-gradient(135deg, #3D2ECC 0%, #221840 100%)',
} as const

export type BrandPaletteKey  = keyof typeof brandPalette
export type BrandGradientKey = keyof typeof brandGradient

export type Tokens = typeof tokens
export type VioletShade = keyof typeof tokens.color.violet
export type MintShade   = keyof typeof tokens.color.mint
export type InkLevel    = keyof typeof tokens.ink
export type SurfaceLevel = keyof typeof tokens.surface
export type StatusKind  = keyof typeof tokens.status
export type TextStyle   = keyof typeof tokens.text
